import { useEffect, useState, type ReactNode } from 'react';
import { api } from '../lib/api.js';
import { taskDueLine } from '../lib/taskDue.js';
import { useFetch } from '../lib/useFetch.js';
import { apptStatusInfo, type ApptTone } from '../lib/apptStatus.js';
import { calendarApptHref } from '../lib/apptLink.js';
import { useLiveRefresh, useLiveVersion } from '../lib/useLiveRefresh.js';
import { NOTES_REMOTE, TASKS_CHANGED, TASKS_REMOTE } from '../lib/liveEvents.js';
import { Async } from '../components/States.js';
import { Modal } from '../components/Modal.js';
import { useSession, displayName } from '../auth/session.js';
import { PartTwoBanner } from '../components/PartTwoBanner.js';
import { messagesUrl, useMessagesUnread } from '../lib/messages.js';
import { NewContactModal, baseTypeForCategory, type ContactTypeRow } from './ContactsTab.js';
import { timeOpts } from '../lib/timePrefs.js';
import { NoteStatusPill } from '../components/NoteStatusPill.js';

/** Dashboard summary — shape mirrors the revised mockup (ehr-revised · renderDashboard). */
interface Appt {
  id: string;
  /** calendar#46 step 9: the calendar event; group roster rows are "<eventId>:<dateKey>". */
  eventId?: string;
  isGroup?: boolean;
  contactId: string | null;
  providerId: string | null;
  startsAt: string;
  modality: string;
  /** ehr#414: the appointment's own video room (telehealth only). */
  videoUrl?: string | null;
  status: string;
  confirmed: boolean;
  contactName: string;
  type: string;
  duration: number;
  /** ehr#525: its progress note's status, null when none yet; absent for roles without notes. */
  noteStatus?: string | null;
}
// #86: an appointment behind the confirmations widget (lighter than Appt; drives
// the click-through popup list).
interface ConfAppt {
  id: string;
  contactId: string | null;
  startsAt: string;
  modality: string;
  videoUrl?: string | null; // ehr#414
  confirmed: boolean;
  contactName: string;
  type: string;
  duration: number;
}
interface FormItem {
  id: string;
  formName: string;
  patientName: string;
  patientId: string | null;
  dueAt: string | null;
}
interface ClientFormItem extends FormItem {
  sentVia: string;
}
/** ehr#485: a recent progress note (metadata only). */
interface NoteItem {
  id: string;
  contactId: string;
  contactName: string;
  /** The session day (saved at midnight UTC). */
  date: string;
  status: string;
  sessionLabel: string;
  updatedAt: string;
}
/** A note's session day (saved as that day at midnight UTC), e.g. "Oct 17". */
const noteDay = (iso: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m
    ? new Date(+m[1]!, +m[2]! - 1, +m[3]!).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
    : '';
};
interface TaskItem {
  id: string;
  title: string;
  priority: string;
  dueAt: string | null;
}
interface Summary {
  tiles: {
    activeClients: number;
    myForms: number;
    clientForms: number;
    unreadMessages: number;
    openTasks: number;
  };
  todaysScheduleTotal: number;
  // Today's appointment-confirmation counts + the appointments behind them, so the
  // widget can open a click-through list (calendar #11 / ehr #86).
  confirmations: {
    confirmed: number;
    notConfirmed: number;
    total: number;
    appointments: ConfAppt[];
  };
  todaysSchedule: Appt[];
  myForms: FormItem[];
  clientForms: ClientFormItem[];
  recentNotes: NoteItem[];
  openTasks: TaskItem[];
}

const MY_FORMS_CAP = 4; // mockup FORM_CAP — rows shown before "Show all"

const time = (s: string) => new Date(s).toLocaleTimeString('en-US', timeOpts());
const d = (s: string) =>
  new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const isOverdue = (due: string | null) => {
  if (!due) return false;
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return new Date(due) < t;
};

// Modality → badge + row-icon colors (mockup apptRow).
type Tone = { label: string; color: string; bg: string };
const IN_PERSON: Tone = { label: 'In-person', color: '#1a8a66', bg: '#edfaf4' };
const MODALITY: Record<string, Tone> = {
  telehealth: { label: 'Telehealth', color: '#6366f1', bg: '#eef2ff' },
  hybrid: { label: 'Hybrid', color: '#d97706', bg: '#fffbeb' },
  in_person: IN_PERSON,
};
const modalityOf = (m: string): Tone => MODALITY[m] ?? IN_PERSON;
// calendar#61: the status is the Calendar's own word ('client-cancelled', 'no-show',
// 'checked-in', 'checked-out', …); apptStatusInfo reads them all (and the older
// 'no_show' / 'cancelled'). Comparing against 'cancelled' alone missed every Calendar
// cancellation.
const isCancelled = (a: Appt) => apptStatusInfo(a.status).cancelled;

// #47: second badge for Today's Schedule (mockup apptConfirmBadgeInfo), shown
// alongside the modality badge.
const STATUS_TONE: Partial<Record<ApptTone, Tone>> = {
  completed: { label: 'Completed', color: '#527060', bg: '#edfaf4' },
  noshow: { label: 'No-show', color: '#b91c1c', bg: '#fee2e2' },
  cancelled: { label: 'Cancelled', color: '#8a5252', bg: '#f4e9e9' },
  checkedin: { label: 'Checked in', color: '#0f766e', bg: '#dcfce7' },
  checkedout: { label: 'Checked out', color: '#1d4ed8', bg: '#e0e7ff' },
};
const apptStatusBadge = (a: Appt): Tone => {
  const info = apptStatusInfo(a.status);
  const tone = STATUS_TONE[info.tone];
  // Cancellations keep their own wording (Client cancelled, Late cancel, …).
  if (tone) return info.tone === 'cancelled' ? { ...tone, label: info.label } : tone;
  if (a.confirmed) return { label: 'Confirmed', color: '#1a8a66', bg: '#edfaf4' };
  return { label: 'Not confirmed', color: '#b91c1c', bg: '#fee2e2' };
};

// #47: group same-slot appointments (same start time + provider) so a cancelled
// booking and whatever else landed in that slot render together. Cancelled first.
const groupApptSlots = (list: Appt[]): Appt[][] => {
  const groups: Appt[][] = [];
  const byKey = new Map<string, number>();
  for (const a of list) {
    const key = `${a.startsAt}|${a.providerId ?? ''}`;
    const idx = byKey.get(key);
    if (idx != null) groups[idx]!.push(a);
    else {
      byKey.set(key, groups.length);
      groups.push([a]);
    }
  }
  for (const g of groups)
    if (g.length > 1)
      g.sort((x, y) => (x.status === 'cancelled' ? 0 : 1) - (y.status === 'cancelled' ? 0 : 1));
  return groups;
};

const scrollTo = (id: string) =>
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

const svg = (p: string) =>
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${p}</svg>`;
const PATH = {
  users:
    '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  form: '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>',
  formCheck:
    '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><polyline points="9 11 12 14 22 4"/>',
  message:
    '<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>',
  check:
    '<polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>',
  cal: '<rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>',
  note: '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>',
  plus: '<line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>',
};
const ICON = Object.fromEntries(Object.entries(PATH).map(([k, p]) => [k, svg(p)])) as Record<
  keyof typeof PATH,
  string
>;
const Svg = ({ p }: { p: string }) => <span aria-hidden dangerouslySetInnerHTML={{ __html: p }} />;
// Row icon with mockup's per-row bg + stroke color (overrides the .green default).
const RowIcon = ({ path, color, bg }: { path: string; color: string; bg?: string }) => (
  <div className="dash-panel-row-icon" style={bg ? { background: bg } : undefined}>
    <span
      aria-hidden
      style={{ color, display: 'flex' }}
      dangerouslySetInnerHTML={{
        __html: `<svg viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${path}</svg>`,
      }}
    />
  </div>
);

// #86: right-aligned chevron marking a confirmation row as a click-through.
const Chevron = () => (
  <span
    aria-hidden
    className="dash-panel-row-chevron"
    dangerouslySetInnerHTML={{
      __html: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>`,
    }}
  />
);

type Props = {
  onNavigate: (surface: string) => void;
  /** Deep-link into a contact's drawer on a specific tab (Dashboard → Contacts). */
  onOpenContact: (id: string, view?: string) => void;
  /** calendar#46 step 9: open the group note workspace on one attendee. */
  onOpenGroupNote?: (appointmentKey: string, focus: string | null) => void;
  /** ehr#485: open a progress note in the client's Clinical Notes. */
  onOpenNote?: (contactId: string, noteId: string) => void;
};

const CLINICAL_ROLES = new Set(['admin', 'manager', 'clinician', 'supervisor']);
const pad2 = (n: number) => String(n).padStart(2, '0');
const localDateKey = (iso: string) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

/** Dashboard surface — pixel-port of the revised mockup's renderDashboard, on live data. */
export function DashboardTab({ onNavigate, onOpenContact, onOpenGroupNote, onOpenNote }: Props) {
  const state = useFetch<Summary>(() => api.get('/dashboard/summary'), []);
  // ehr#86 - live: a confirmation or any change to today's appointments made in the Calendar
  // or the portal shows within ~3 s (a tiny version check; the summary reloads only when it
  // changes), and the whole Dashboard (tasks, forms) refreshes every 20 s and on coming back.
  useLiveVersion(() => api.get<{ version: string }>('/dashboard/version'), state.refresh);
  useLiveRefresh(state.refresh);
  // ehr#408: a progress note saved anywhere (a group note's attendees, another tab) reaches Recent
  // Progress Notes within seconds, not at the next 20 s refresh
  useEffect(() => {
    const reload = () => state.refresh();
    window.addEventListener(NOTES_REMOTE, reload);
    // ehr#659: Open Tasks and the Tasks panel follow the tasks stamp the shell checks every 3 s
    // (a task changed in another tab or by a teammate) and this tab's own task saves, as the
    // Tasks page and the sidebar badge do; it waited for the 20 s refresh, so tabs disagreed.
    window.addEventListener(TASKS_REMOTE, reload);
    window.addEventListener(TASKS_CHANGED, reload);
    return () => {
      window.removeEventListener(NOTES_REMOTE, reload);
      window.removeEventListener(TASKS_REMOTE, reload);
      window.removeEventListener(TASKS_CHANGED, reload);
    };
  }, [state.refresh]);
  const { user, impersonating } = useSession();
  // messaging#24 - the Unread Messages tile reads Messaging (the summary has no feed for it).
  const unreadMessages = useMessagesUnread(Boolean(user));
  // calendar#46 step 9: "Note" on a Today's Schedule row. A 1:1 appointment opens its
  // progress note in the client's Clinical Notes (the calendar's appointment link: the
  // note it already has, or a new one pre-filled); a group row opens the group workspace
  // on that attendee.
  const canNote = CLINICAL_ROLES.has(user?.role ?? '');
  const openNote = (a: Appt) => {
    if (!a.contactId || !a.eventId) return;
    if (a.isGroup) {
      const dateKey = a.id.slice(a.id.lastIndexOf(':') + 1);
      onOpenGroupNote?.(`${a.eventId}@${dateKey}`, a.contactId);
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.set('appt', `${a.eventId}@${localDateKey(a.startsAt)}`);
    url.searchParams.set('appt_time', time(a.startsAt));
    url.searchParams.set('appt_type', a.type);
    url.searchParams.set('appt_dur', String(a.duration));
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    onOpenContact(a.contactId, 'clinical_notes');
  };
  const [showNew, setShowNew] = useState(false);
  // #37: "Add Client" opens the New-client window straight away, on the Client type.
  const [newType, setNewType] = useState<ContactTypeRow | null>(null);
  const contactTypes = useFetch<ContactTypeRow[]>(() => api.get('/contact-types'), []);
  const [showAllForms, setShowAllForms] = useState(false);
  // #86: which confirmation bucket the click-through popup is showing (null = closed).
  const [confBucket, setConfBucket] = useState<'confirmed' | 'notConfirmed' | null>(null);
  const hour = new Date().getHours();
  const greet = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  // ehr#264 - greet by first name, not the login email.
  const name = user?.name?.trim().split(/\s+/)[0] || displayName(null, user?.email);
  const date = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <>
      <Async state={state}>
        {(s) => {
          const total = s.todaysScheduleTotal;
          const shown = s.todaysSchedule.length;
          const scheduleCount =
            shown < total
              ? `${shown} of ${total} appointments`
              : `${total} appointment${total === 1 ? '' : 's'}`;
          const apptRow = (a: Appt) => {
            const m = modalityOf(a.modality);
            const st = apptStatusBadge(a);
            return (
              <div
                key={a.id}
                className={`dash-panel-row${isCancelled(a) ? ' dash-row-cancelled' : ''}`}
                // #101: a clientless appointment has no contact drawer to open, so it is
                // shown ("No client") but not clickable and carries no TP link.
                style={{ cursor: a.contactId ? 'pointer' : 'default' }}
                onClick={
                  a.contactId ? () => onOpenContact(a.contactId!, 'appointments') : undefined
                }
              >
                <RowIcon path={PATH.cal} color={m.color} bg={m.bg} />
                <div className="dash-panel-row-info">
                  <div className="dash-panel-row-name">{a.contactName}</div>
                  <div className="dash-panel-row-sub">
                    {a.type} · {a.duration} min
                    {/* ehr#525: the progress note's status; "No note" once the session has
                        started (a later one has nothing to write yet), none if cancelled */}
                    {a.noteStatus !== undefined &&
                      !isCancelled(a) &&
                      (a.noteStatus ? (
                        <NoteStatusPill status={a.noteStatus} className="cn-badge dash-note-pill" />
                      ) : (
                        new Date(a.startsAt) <= new Date() && (
                          <span className="cn-badge ns-other dash-note-pill">No note</span>
                        )
                      ))}
                  </div>
                </div>
                <div
                  className="dash-panel-row-action"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                  }}
                >
                  {a.videoUrl && !isCancelled(a) && (
                    // ehr#414: the appointment's own video room, as in the Calendar
                    <a
                      className="dash-tp-link"
                      href={a.videoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`Join the video room: ${a.videoUrl}`}
                      style={{ textDecoration: 'none' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      Join
                    </a>
                  )}
                  {canNote && a.contactId && a.eventId && !isCancelled(a) && (
                    <button
                      type="button"
                      className="dash-tp-link"
                      title={
                        a.isGroup
                          ? 'Open the group note for this session'
                          : "Open this appointment's progress note"
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        openNote(a);
                      }}
                    >
                      Note
                    </button>
                  )}
                  {/* tp#299 (Jay 11-03): the TP button that opened the client's treatment plan
                      from Today's Schedule is removed. A plan opens only from an active client's
                      chart (Clinical Notes > Treatment plans). */}
                  <Badge {...m} />
                  <Badge {...st} />
                  <span className="dash-appt-time">{time(a.startsAt)}</span>
                </div>
              </div>
            );
          };
          const visibleForms = s.myForms.slice(0, MY_FORMS_CAP);
          const overflowForms = s.myForms.slice(MY_FORMS_CAP);

          return (
            <div className="dashboard">
              <div className="dashboard-header">
                <div className="dashboard-greeting">
                  <h1 className="dashboard-greeting-h">
                    {greet}
                    {name ? `, ${name}` : ''}
                  </h1>
                  <div className="dashboard-greeting-date">{date}</div>
                </div>
                <div className="dashboard-quick-actions">
                  <button
                    className="btn primary"
                    onClick={() => {
                      const t = baseTypeForCategory('client', contactTypes.data ?? []);
                      if (t) {
                        setNewType(t);
                        setShowNew(true);
                      }
                    }}
                  >
                    <Svg p={ICON.plus} />
                    Add Client
                  </button>
                </div>
              </div>

              {/* credify-platform#306: Onboarding Part Two not finished (admins only) */}
              <PartTwoBanner />

              <div className="dashboard-stats">
                <Stat
                  tone="green"
                  icon={ICON.users}
                  value={s.tiles.activeClients}
                  label="Active Clients"
                  onClick={() => onNavigate('contacts')}
                />
                <Stat
                  tone="amber"
                  icon={ICON.form}
                  value={s.tiles.myForms}
                  label="My Forms to Complete"
                  onClick={() => scrollTo('dash-my-forms')}
                />
                <Stat
                  tone="blue"
                  icon={ICON.formCheck}
                  value={s.tiles.clientForms}
                  label="Client Forms to Complete"
                  onClick={() => scrollTo('dash-client-forms')}
                />
                <Stat
                  tone="purple"
                  icon={ICON.message}
                  value={unreadMessages ?? s.tiles.unreadMessages}
                  label="Unread Messages"
                  onClick={() =>
                    window.open(
                      messagesUrl(impersonating ? displayName(user?.name, user?.email) : null),
                      '_blank',
                    )
                  }
                />
                <Stat
                  tone="green"
                  icon={ICON.check}
                  value={s.tiles.openTasks}
                  label="Open Tasks"
                  onClick={() => onNavigate('tasks')}
                />
              </div>

              <div className="dashboard-body">
                <div className="dashboard-col">
                  {/* Appointment Confirmations (today) — calendar #11 / ehr #86.
                      Shown only when there are appointments today. */}
                  {s.confirmations.total > 0 && (
                    <Panel
                      title="Appointment Confirmations"
                      count={`${s.confirmations.confirmed} of ${s.confirmations.total} confirmed`}
                    >
                      {/* #86: each bucket opens a click-through popup of the appointments
                          behind it, so a user can jump to the ones still awaiting
                          confirmation. Rendered as buttons for keyboard access. */}
                      <button
                        type="button"
                        className="dash-panel-row dash-panel-row-btn"
                        onClick={() => setConfBucket('confirmed')}
                        disabled={s.confirmations.confirmed === 0}
                      >
                        <RowIcon path={PATH.check} color="#1a8a66" bg="#edfaf4" />
                        <div className="dash-panel-row-info">
                          <div className="dash-panel-row-name">
                            {s.confirmations.confirmed} confirmed
                          </div>
                          <div className="dash-panel-row-sub">
                            Client confirmed today’s appointment
                          </div>
                        </div>
                        {s.confirmations.confirmed > 0 && <Chevron />}
                      </button>
                      <button
                        type="button"
                        className="dash-panel-row dash-panel-row-btn"
                        onClick={() => setConfBucket('notConfirmed')}
                        disabled={s.confirmations.notConfirmed === 0}
                      >
                        <RowIcon path={PATH.cal} color="#b97800" bg="#fff8ec" />
                        <div className="dash-panel-row-info">
                          <div className="dash-panel-row-name">
                            {s.confirmations.notConfirmed} not confirmed
                          </div>
                          <div className="dash-panel-row-sub">Still awaiting confirmation</div>
                        </div>
                        {s.confirmations.notConfirmed > 0 && <Chevron />}
                      </button>
                    </Panel>
                  )}

                  {/* Today's Schedule (#47: modality + status badges, cancelled dimmed, same-slot grouped) */}
                  <Panel title="Today’s Schedule" count={scheduleCount}>
                    {shown === 0 ? (
                      <Empty>No appointments scheduled for today</Empty>
                    ) : (
                      groupApptSlots(s.todaysSchedule).map((group) =>
                        group.length > 1 ? (
                          <div className="dash-slot-group" key={group[0]!.id}>
                            <div className="dash-slot-group-head">Same time slot</div>
                            {group.map((a) => apptRow(a))}
                          </div>
                        ) : (
                          apptRow(group[0]!)
                        ),
                      )
                    )}
                  </Panel>

                  {/* My Forms to Complete */}
                  <div id="dash-my-forms">
                    <Panel title="My Forms to Complete" count={`${s.myForms.length} pending`}>
                      {s.myForms.length === 0 ? (
                        <Empty>No forms waiting for your sign-off</Empty>
                      ) : (
                        <>
                          {visibleForms.map((f) => (
                            <MyFormRow key={f.id} f={f} onOpen={() => onNavigate('signoff')} />
                          ))}
                          {overflowForms.length > 0 && (
                            <>
                              <div
                                className={`dash-panel-overflow${showAllForms ? ' expanded' : ''}`}
                              >
                                {overflowForms.map((f) => (
                                  <MyFormRow
                                    key={f.id}
                                    f={f}
                                    onOpen={() => onNavigate('signoff')}
                                  />
                                ))}
                              </div>
                              <button
                                className="dash-show-all-btn"
                                onClick={() => setShowAllForms((v) => !v)}
                              >
                                {showAllForms ? 'Show less' : `Show all ${s.myForms.length} forms`}
                              </button>
                            </>
                          )}
                        </>
                      )}
                    </Panel>
                  </div>

                  {/* Client Forms to Complete */}
                  <div id="dash-client-forms">
                    <Panel
                      title="Client Forms to Complete"
                      count={`${s.tiles.clientForms} awaiting clients`}
                    >
                      {s.clientForms.length === 0 ? (
                        <Empty>No forms waiting on clients</Empty>
                      ) : (
                        s.clientForms.map((f) => (
                          <div className="dash-panel-row" key={f.id}>
                            <RowIcon path={PATH.formCheck} color="#3b72d9" bg="#eef3ff" />
                            <div className="dash-panel-row-info">
                              <div className="dash-panel-row-name">
                                {f.patientName} — {f.formName}
                              </div>
                              <div className="dash-panel-row-sub">
                                Sent via {f.sentVia}
                                {f.dueAt && (
                                  <>
                                    {' · '}
                                    <span
                                      style={{
                                        color: isOverdue(f.dueAt) ? '#dc2626' : 'var(--text-muted)',
                                      }}
                                    >
                                      Due {d(f.dueAt)}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="dash-panel-row-action">
                              <button className="btn ghost-sm" onClick={() => onNavigate('send')}>
                                Remind
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                      {/* ehr#486: the card shows the 5 newest; the rest are in Forms Hub → Send */}
                      {s.tiles.clientForms > s.clientForms.length && (
                        <div className="dash-viewall" onClick={() => onNavigate('send')}>
                          View all {s.tiles.clientForms}
                        </div>
                      )}
                    </Panel>
                  </div>
                </div>

                <div className="dashboard-col">
                  {/* Recent Clinical Notes — metadata only (note bodies are server-gated PHI) */}
                  <div className="dash-panel">
                    <div className="dash-panel-head">
                      <span className="dash-panel-title">Recent Progress Notes</span>
                      <span className="dash-panel-count">{s.recentNotes.length} recent</span>
                    </div>
                    <div className="dash-panel-body">
                      {s.recentNotes.length === 0 ? (
                        <Empty>No recent notes</Empty>
                      ) : (
                        s.recentNotes.map((n) => (
                          <div className="dash-panel-row" key={n.id}>
                            <RowIcon path={PATH.note} color="#1a8a66" bg="#edfaf4" />
                            <div className="dash-panel-row-info">
                              <div className="dash-panel-row-name">{n.contactName}</div>
                              <div className="dash-panel-row-sub">
                                {[n.sessionLabel, noteDay(n.date)].filter(Boolean).join(' · ')}
                                {/* ehr#525: the status as the colored note pill (ehr#477) */}
                                <NoteStatusPill
                                  status={n.status}
                                  className="cn-badge dash-note-pill"
                                />
                              </div>
                            </div>
                            <div className="dash-panel-row-action">
                              <button
                                className="btn ghost-sm"
                                onClick={() =>
                                  onOpenNote
                                    ? onOpenNote(n.contactId, n.id)
                                    : onOpenContact(n.contactId, 'clinical_notes')
                                }
                              >
                                View
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    {s.recentNotes.length > 0 && (
                      <div className="dash-viewall" onClick={() => onNavigate('notes')}>
                        View all progress notes
                      </div>
                    )}
                  </div>

                  {/* Tasks */}
                  <div className="dash-panel">
                    <div className="dash-panel-head">
                      <span className="dash-panel-title">Tasks</span>
                      <span className="dash-panel-count">{s.tiles.openTasks} open</span>
                    </div>
                    <div className="dash-panel-body">
                      {s.openTasks.length === 0 ? (
                        <Empty>No open tasks</Empty>
                      ) : (
                        s.openTasks.map((t) => {
                          // #338: a task with no name reads "(Untitled task)" as on the Tasks
                          // page (the priority word used to stand in for it); #340: overdue in red.
                          const due = taskDueLine(t.dueAt);
                          return (
                            <div className="dash-panel-row" key={t.id}>
                              <RowIcon path={PATH.check} color="#7c3aed" bg="#f5eeff" />
                              <div className="dash-panel-row-info">
                                <div className="dash-panel-row-name">
                                  {t.title?.trim() || '(Untitled task)'}
                                </div>
                                <div
                                  className="dash-panel-row-sub"
                                  style={
                                    due.late ? { color: '#dc2626', fontWeight: 600 } : undefined
                                  }
                                >
                                  {due.text}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                    {s.openTasks.length > 0 && (
                      <div className="dash-viewall" onClick={() => onNavigate('tasks')}>
                        View all tasks
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* #86: click-through popup listing the appointments behind a confirmation
                  bucket. Each row deep-links to the client's Appointments drawer, where
                  the confirmation status can be reviewed/changed. */}
              {confBucket && (
                <ConfirmationsModal
                  bucket={confBucket}
                  appts={s.confirmations.appointments}
                  onClose={() => setConfBucket(null)}
                  onOpenContact={(id) => {
                    setConfBucket(null);
                    onOpenContact(id, 'appointments');
                  }}
                />
              )}
            </div>
          );
        }}
      </Async>
      {showNew && newType && (
        <NewContactModal
          contactType={newType}
          onClose={() => setShowNew(false)}
          onCreated={(newId) => {
            setShowNew(false);
            // Pop open the new client's Demographics drawer, pre-filled.
            if (newId) onOpenContact(newId, 'demographics');
          }}
          onOpenExisting={(id) => {
            setShowNew(false);
            onOpenContact(id);
          }}
        />
      )}
    </>
  );
}

// #86: the click-through list behind a confirmation bucket. Read-only here (the
// confirmation flag is owned by the Calendar module); each row opens the client's
// Appointments drawer to act on it.
function ConfirmationsModal({
  bucket,
  appts,
  onClose,
  onOpenContact,
}: {
  bucket: 'confirmed' | 'notConfirmed';
  appts: ConfAppt[];
  onClose: () => void;
  onOpenContact: (id: string) => void;
}) {
  const wantConfirmed = bucket === 'confirmed';
  const list = appts
    .filter((a) => a.confirmed === wantConfirmed)
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  const title = wantConfirmed ? 'Confirmed appointments' : 'Appointments awaiting confirmation';
  // #86: clicking a list item opens a detail pop-up for that specific appointment,
  // from which it can be opened in the Calendar (where the confirmation status is
  // owned) or in the client's Appointments drawer.
  const [detail, setDetail] = useState<ConfAppt | null>(null);
  return (
    <>
      <Modal
        title={title}
        onClose={onClose}
        actions={
          <button className="btn" onClick={onClose}>
            Close
          </button>
        }
      >
        {list.length === 0 ? (
          <p className="muted">No appointments in this list for today.</p>
        ) : (
          <div className="dash-conf-list">
            {list.map((a) => {
              const m = modalityOf(a.modality);
              return (
                <button
                  type="button"
                  key={a.id}
                  className="dash-panel-row dash-panel-row-btn"
                  onClick={() => setDetail(a)}
                  title="Open this appointment"
                >
                  <RowIcon path={PATH.cal} color={m.color} bg={m.bg} />
                  <div className="dash-panel-row-info">
                    <div className="dash-panel-row-name">{a.contactName}</div>
                    <div className="dash-panel-row-sub">
                      {a.type} · {a.duration} min
                    </div>
                  </div>
                  <div
                    className="dash-panel-row-action"
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    <Badge {...m} />
                    <span className="dash-appt-time">{time(a.startsAt)}</span>
                    <Chevron />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </Modal>
      {detail && (
        <ApptDetailModal
          appt={detail}
          onClose={() => setDetail(null)}
          onOpenContact={onOpenContact}
        />
      )}
    </>
  );
}

// #86: the per-appointment pop-up opened from a confirmation list item. Read-only
// summary plus the two ways to act on it — open it in the Calendar (which owns the
// confirmation status) or jump to the client's Appointments drawer.
function ApptDetailModal({
  appt,
  onClose,
  onOpenContact,
}: {
  appt: ConfAppt;
  onClose: () => void;
  onOpenContact: (id: string) => void;
}) {
  const m = modalityOf(appt.modality);
  const conf: Tone = appt.confirmed
    ? { label: 'Confirmed', color: '#1a8a66', bg: '#edfaf4' }
    : { label: 'Not confirmed', color: '#b91c1c', bg: '#fee2e2' };
  return (
    <Modal
      title={appt.contactName}
      onClose={onClose}
      actions={
        <>
          <button className="btn" onClick={onClose}>
            Back
          </button>
          {appt.contactId && (
            <button className="btn" onClick={() => onOpenContact(appt.contactId!)}>
              Open client
            </button>
          )}
          <button
            className="btn primary"
            onClick={
              () => window.open(calendarApptHref(appt.id), '_blank') // #86, ehr#317: the Calendar's ?appt=
            }
          >
            Open in Calendar
          </button>
        </>
      }
    >
      <div className="dash-conf-detail">
        <div className="dash-conf-detail-row">
          <span className="muted">When</span>
          <span>
            {d(appt.startsAt)} · {time(appt.startsAt)}
          </span>
        </div>
        <div className="dash-conf-detail-row">
          <span className="muted">Type</span>
          <span>
            {appt.type} · {appt.duration} min
          </span>
        </div>
        <div className="dash-conf-detail-row">
          <span className="muted">Modality</span>
          <Badge {...m} />
        </div>
        {appt.videoUrl && (
          // ehr#414
          <div className="dash-conf-detail-row">
            <span className="muted">Video room</span>
            <a href={appt.videoUrl} target="_blank" rel="noopener noreferrer">
              Join video
            </a>
          </div>
        )}
        <div className="dash-conf-detail-row">
          <span className="muted">Confirmation</span>
          <Badge {...conf} />
        </div>
      </div>
      {!appt.confirmed && (
        <p className="muted" style={{ marginTop: 12 }}>
          Confirmation is set in the Calendar. Open it there to confirm or reschedule.
        </p>
      )}
    </Modal>
  );
}

function MyFormRow({ f, onOpen }: { f: FormItem; onOpen: () => void }) {
  return (
    <div className="dash-panel-row" style={{ cursor: 'pointer' }} onClick={onOpen}>
      <RowIcon path={PATH.form} color="#b97800" bg="#fff8ec" />
      <div className="dash-panel-row-info">
        <div className="dash-panel-row-name">{f.formName}</div>
        <div className="dash-panel-row-sub">
          {f.patientName}
          {f.dueAt && (
            <>
              {' · '}
              <span style={{ color: isOverdue(f.dueAt) ? '#dc2626' : 'var(--text-muted)' }}>
                Due {d(f.dueAt)}
              </span>
            </>
          )}
        </div>
      </div>
      <div className="dash-panel-row-action">
        <button
          className="btn ghost-sm"
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
        >
          Open
        </button>
      </div>
    </div>
  );
}

function Badge({ label, color, bg }: { label: string; color: string; bg: string }) {
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        color,
        background: bg,
        border: `1px solid ${color}22`,
        padding: '2px 7px',
        borderRadius: 20,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

function Stat({
  tone,
  icon,
  value,
  label,
  onClick,
}: {
  tone: string;
  icon: string;
  value: number;
  label: string;
  onClick?: () => void;
}) {
  return (
    <div className={`dash-stat${onClick ? ' dash-stat-link' : ''}`} onClick={onClick}>
      <div className="dash-stat-top">
        <div className={`dash-stat-icon ${tone}`}>
          <Svg p={icon} />
        </div>
        <div className="dash-stat-value">{value}</div>
      </div>
      <div className="dash-stat-label">{label}</div>
    </div>
  );
}

function Panel({ title, count, children }: { title: string; count: string; children: ReactNode }) {
  return (
    <div className="dash-panel">
      <div className="dash-panel-head">
        <span className="dash-panel-title">{title}</span>
        <span className="dash-panel-count">{count}</span>
      </div>
      <div className="dash-panel-body">{children}</div>
    </div>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return <div className="dash-panel-empty">{children}</div>;
}
